/**
 * RichTextToolbar — barre de mise en forme + sélecteur d'emoji.
 *
 * Écrit du markdown directement dans le <textarea> : le contenu reste du
 * texte brut en base, ce qui préserve la traduction automatique
 * (translation.py demande explicitement au modèle de conserver le markdown
 * et les emoji), les e-mails Brevo et les extraits du fil.
 *
 * Aucune dépendance npm ajoutée : le sélecteur d'emoji est une simple grille
 * de caractères Unicode. Les emoji sont du texte, ils traversent PostgreSQL,
 * l'API et le rendu sans traitement particulier.
 *
 * Accessibilité : chaque bouton porte un aria-label ; la palette se ferme
 * à l'Échap et au clic extérieur.
 */
import { useEffect, useRef, useState } from "react";
import { Bold, Italic, List, ListOrdered, Smile, Strikethrough } from "lucide-react";
import { insertAt, togglePrefix, toggleWrap } from "../lib/markdown";

const EMOJI_GROUPS = [
  {
    label: "Fréquents",
    emojis: ["👍", "🙌", "👏", "🎉", "🔥", "✅", "💡", "❤️", "😊", "🚀", "⭐", "🙏"],
  },
  {
    label: "Réactions",
    emojis: ["😀", "😃", "😉", "😍", "🤩", "😎", "🤔", "😅", "😢", "😮", "🤝", "💪"],
  },
  {
    label: "Business",
    emojis: ["📈", "📉", "📊", "💼", "🏆", "🎯", "🤞", "📌", "📅", "📣", "🗓️", "✍️"],
  },
  {
    label: "Réseau APOLLO",
    emojis: ["🌍", "🌎", "🌏", "🏢", "🤗", "🍾", "🥂", "☕", "🎓", "🔗", "📍", "✨"],
  },
];

export default function RichTextToolbar({ textareaRef, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /** Applique une transformation puis restaure la sélection.
   *  Sans ça, le curseur saute en fin de champ à chaque clic. */
  const apply = (fn) => {
    const ta = textareaRef?.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    const next = fn(value ?? "", s, e);
    onChange(next.value);
    requestAnimationFrame(() => {
      const el = textareaRef?.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(next.start, next.end);
    });
  };

  const btn =
    "flex items-center justify-center w-8 h-8 rounded-md border bg-white transition-colors hover:bg-[#FFF7ED] disabled:opacity-40";

  return (
    <div className="flex items-center gap-1.5 flex-wrap mb-2 relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => apply((v, s, e) => toggleWrap(v, s, e, "**"))}
        className={btn}
        style={{ borderColor: "#E5E5E5" }}
        aria-label="Gras"
        title="Gras"
        data-testid="rt-bold"
      >
        <Bold size={14} />
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => apply((v, s, e) => toggleWrap(v, s, e, "*"))}
        className={btn}
        style={{ borderColor: "#E5E5E5" }}
        aria-label="Italique"
        title="Italique"
        data-testid="rt-italic"
      >
        <Italic size={14} />
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => apply((v, s, e) => toggleWrap(v, s, e, "~~"))}
        className={btn}
        style={{ borderColor: "#E5E5E5" }}
        aria-label="Barré"
        title="Barré"
        data-testid="rt-strike"
      >
        <Strikethrough size={14} />
      </button>

      <span className="w-px h-5 mx-0.5" style={{ background: "#E5E5E5" }} />

      <button
        type="button"
        disabled={disabled}
        onClick={() => apply((v, s, e) => togglePrefix(v, s, e, "- "))}
        className={btn}
        style={{ borderColor: "#E5E5E5" }}
        aria-label="Liste à puces"
        title="Liste à puces"
        data-testid="rt-ul"
      >
        <List size={14} />
      </button>
      <button
        type="button"
        disabled={disabled}
        onClick={() => apply((v, s, e) => togglePrefix(v, s, e, "1. "))}
        className={btn}
        style={{ borderColor: "#E5E5E5" }}
        aria-label="Liste numérotée"
        title="Liste numérotée"
        data-testid="rt-ol"
      >
        <ListOrdered size={14} />
      </button>

      <span className="w-px h-5 mx-0.5" style={{ background: "#E5E5E5" }} />

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={btn}
        style={{
          borderColor: open ? "#F77F00" : "#E5E5E5",
          background: open ? "#FFF7ED" : "#FFF",
        }}
        aria-label="Insérer un emoji"
        aria-expanded={open}
        title="Emoji"
        data-testid="rt-emoji"
      >
        <Smile size={14} color={open ? "#F77F00" : undefined} />
      </button>

      <span className="text-[11px] ml-1" style={{ color: "#999" }}>
        Gras, italique et listes s'affichent une fois publié
      </span>

      {open && (
        <div
          ref={popRef}
          className="absolute z-30 top-10 left-0 p-3 rounded-[12px] bg-white shadow-xl w-[280px] max-h-[260px] overflow-y-auto"
          style={{ border: "1px solid #E5E5E5" }}
          data-testid="rt-emoji-panel"
        >
          {EMOJI_GROUPS.map((g) => (
            <div key={g.label} className="mb-2 last:mb-0">
              <div
                className="text-[10px] font-bold uppercase tracking-wider mb-1"
                style={{ color: "#999" }}
              >
                {g.label}
              </div>
              <div className="grid grid-cols-6 gap-1">
                {g.emojis.map((em) => (
                  <button
                    key={em}
                    type="button"
                    onClick={() => {
                      apply((v, s, e) => insertAt(v, s, e, em));
                      setOpen(false);
                    }}
                    className="h-8 rounded-md text-lg leading-none hover:bg-[#FFF7ED] transition-colors"
                    aria-label={`Insérer ${em}`}
                    data-testid={`rt-emoji-${em}`}
                  >
                    {em}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
